import { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { ventures, getVenture } from "../data/ventures";
import { MaskLines, FadeUp } from "../components/Reveal";

const ease = [0.22, 1, 0.36, 1];

const VentureHero = ({ v, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.25, 1]);
  return (
    <section className="grain relative bg-ink px-5 pb-16 pt-32 text-bone md:px-10 md:pt-40">
      <div className="relative mx-auto max-w-[1500px]">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="eyebrow flex flex-wrap items-center gap-3 text-bone/75">
          <span className="text-[var(--accent)]">0{index + 1} / 0{ventures.length}</span>
          <span className="h-px w-8 bg-bone/30" />
          <span data-testid="venture-kind">{v.kind}</span>
          <span className="h-px w-8 bg-bone/30" />
          <span>{v.sector}</span>
        </motion.p>
        <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8">
        <motion.img
          initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, ease, delay: 0.2 }}
          src={v.logo}
          alt={`${v.name} logo`}
          data-testid="venture-page-logo"
          className="h-24 w-24 shrink-0 rounded-3xl bg-white object-cover shadow-2xl ring-1 ring-bone/20 sm:h-32 sm:w-32 lg:h-40 lg:w-40"
        />
        <h1 data-testid="venture-name" className="min-w-0 break-words font-display text-[10.5vw] font-extrabold uppercase leading-[0.88] tracking-[-0.02em] sm:text-7xl lg:text-[6.5rem]">
          <MaskLines onLoad lines={[v.name]} />
        </h1>
        </div>
        <div className="mt-8 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <MaskLines onLoad delay={2} className="max-w-2xl font-serif text-2xl italic text-[var(--accent)] sm:text-4xl" lines={[v.tagline]} />
          <motion.a
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.6 }}
            href={v.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="venture-visit-site-btn"
            className="group inline-flex shrink-0 items-center gap-3 self-start rounded-full bg-[var(--accent)] px-7 py-4 font-mono text-sm uppercase tracking-[0.14em] text-ink transition-transform duration-300 hover:scale-[1.04] md:self-auto"
          >
            Visit {v.display}
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:rotate-45" />
          </motion.a>
        </div>
        <motion.div
          ref={ref}
          initial={{ clipPath: "inset(18% 12% 18% 12% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          transition={{ duration: 1.5, ease, delay: 0.3 }}
          className="mt-14 aspect-[4/3] overflow-hidden rounded-[28px] md:aspect-[21/9]"
        >
          <motion.img style={{ scale }} src={v.image} alt={v.name} data-testid="venture-hero-image" className="h-full w-full object-cover" />
        </motion.div>
      </div>
    </section>
  );
};

const Body = ({ v }) => (
  <section className="bg-bone px-5 py-24 text-ink md:px-10 md:py-32">
    <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <p className="eyebrow text-ink/60">The mission</p>
        <FadeUp as="p" className="mt-6 font-display text-2xl font-bold leading-[1.2] tracking-tight sm:text-4xl" >
          <span data-testid="venture-intro">{v.intro}</span>
        </FadeUp>
        {v.facts.length > 0 && (
          <div className={`mt-14 grid gap-px overflow-hidden rounded-3xl border border-ink/10 bg-ink/10 ${v.facts.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
            {v.facts.map((f, i) => (
              <FadeUp key={f.label} delay={i * 0.08} className="bg-bone p-4 sm:p-6">
                <div className="font-display text-3xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">{f.value}</div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/65">{f.label}</div>
              </FadeUp>
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-5">
        <p className="eyebrow text-ink/60">{v.offeringsLabel}</p>
        <ul data-testid="venture-offerings" className="mt-6">
          {v.offerings.map((o, i) => (
            <FadeUp as="li" key={o} delay={i * 0.06} className="group flex items-baseline gap-5 border-t border-ink/15 py-5 last:border-b">
              <span className="font-mono text-xs text-ink/55">0{i + 1}</span>
              <span className="font-display text-xl font-bold uppercase tracking-tight transition-transform duration-500 group-hover:translate-x-2 md:text-2xl">{o}</span>
            </FadeUp>
          ))}
        </ul>
        <dl className="mt-12 space-y-4 rounded-3xl bg-ink p-7 text-bone">
          {v.details.map((d) => (
            <div key={d.label} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-6">
              <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-bone/60">{d.label}</dt>
              <dd className="text-sm text-bone sm:text-right">{d.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </section>
);

const Next = ({ next }) => (
  <Link
    to={`/ventures/${next.slug}`}
    data-testid="venture-next-link"
    style={{ "--accent": next.accent }}
    className="group relative block overflow-hidden bg-ink px-5 py-24 text-bone md:px-10 md:py-32"
  >
    <img src={next.image} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-25 transition-[transform,opacity] duration-[1.2s] group-hover:scale-100 group-hover:opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
    <div className="relative mx-auto flex max-w-[1500px] flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <p className="eyebrow text-[var(--accent)]">Next venture</p>
        <p className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl">{next.name}</p>
      </div>
      <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-ink transition-transform duration-500 group-hover:translate-x-3">
        <ArrowRight size={28} />
      </span>
    </div>
  </Link>
);

export default function Venture() {
  const { slug } = useParams();
  const v = getVenture(slug);
  if (!v) return <Navigate to="/" replace />;
  const index = ventures.indexOf(v);
  const next = ventures[(index + 1) % ventures.length];
  return (
    <main key={v.slug} data-testid={`venture-page-${v.slug}`} style={{ "--accent": v.accent }}>
      <VentureHero v={v} index={index} />
      <Body v={v} />
      <Next next={next} />
    </main>
  );
}
